import { config, normalizeUploadsUrlPath } from '@/config';

function apiBaseTrimmed(): string {
  return config.API_URL.trim();
}

/** True si l’API est appelée en relatif (ex. `/api`) → même origine que la SPA, proxy Vite/Nginx pour `/api` et `/uploads`. */
function isRelativeApiBase(): boolean {
  const b = apiBaseTrimmed();
  return !b.startsWith('http://') && !b.startsWith('https://');
}

/**
 * Optionnel : forcer une origine absolue pour `/uploads/...` (contourne le proxy).
 * À n’utiliser que si tu sais ce que tu fais ; laisser vide pour laisser Vite proxy vers `VITE_BACKEND_PROXY_TARGET`.
 */
function explicitUploadsOrigin(): string | null {
  const v = import.meta.env.VITE_DEV_UPLOADS_ORIGIN?.trim().replace(/\/$/, '');
  return v || null;
}

/**
 * Résout une URL de média (`/uploads/...`).
 *
 * - **`VITE_API_URL=/api`** (classique en dev) : retourne un chemin **`/uploads/...`** sur l’origine de la page
 *   (ex. :5173) pour que le **proxy Vite** (`VITE_BACKEND_PROXY_TARGET`) atteigne le backend, quel que soit le port réel.
 * - Les URL absolues stockées en base vers un ancien hôte (ex. `http://localhost:3001/uploads/...`) sont ramenées au
 *   **chemin seul** dans ce mode, pour éviter `ERR_CONNECTION_REFUSED` si rien n’écoute sur :3001.
 * - **`VITE_API_URL=http://hôte:port/api`** : URL absolue vers l’origine de l’API.
 * - **`VITE_DEV_UPLOADS_ORIGIN`** : si défini, préfixe explicite (prioritaire pour les chemins `/uploads/`).
 */
export function resolveUploadsUrl(relativeOrAbsolute: string | null | undefined): string | null {
  if (relativeOrAbsolute == null || relativeOrAbsolute === '') return null;
  const s = normalizeUploadsUrlPath(relativeOrAbsolute.trim());
  const apiBase = apiBaseTrimmed();
  const relativeApi = isRelativeApiBase();

  if (/^https?:\/\//i.test(s)) {
    try {
      const u = new URL(s);
      if (u.pathname.startsWith('/uploads/')) {
        const pathWithQs = `${u.pathname}${u.search}${u.hash}`;
        const forced = explicitUploadsOrigin();
        if (forced && typeof window !== 'undefined') {
          return `${forced}${u.pathname}${u.search}${u.hash}`;
        }
        if (typeof window !== 'undefined') {
          if (relativeApi) {
            return pathWithQs;
          }
          if (u.origin === window.location.origin) {
            return pathWithQs;
          }
          if (apiBase.startsWith('http://') || apiBase.startsWith('https://')) {
            const apiOrigin = apiBase.replace(/\/?api\/?$/i, '').replace(/\/$/, '');
            return `${apiOrigin}${u.pathname}${u.search}${u.hash}`;
          }
          return `${u.origin}${u.pathname}${u.search}${u.hash}`;
        }
        if (apiBase.startsWith('http://') || apiBase.startsWith('https://')) {
          const origin = apiBase.replace(/\/?api\/?$/i, '').replace(/\/$/, '');
          return `${origin}${u.pathname}${u.search}${u.hash}`;
        }
        return pathWithQs;
      }
    } catch {
      /* ignore */
    }
    return s;
  }

  const pathPart = s.startsWith('/') ? s : `/${s}`;

  if (apiBase.startsWith('http://') || apiBase.startsWith('https://')) {
    const origin = apiBase.replace(/\/?api\/?$/i, '').replace(/\/$/, '');
    return `${origin}${pathPart}`;
  }

  const forced = explicitUploadsOrigin();
  if (forced && typeof window !== 'undefined' && pathPart.startsWith('/uploads/')) {
    return `${forced}${pathPart}`;
  }

  return pathPart;
}
